import React, { useEffect, useState, useRef } from 'react';
import { TailSpin } from 'react-loader-spinner';
import SftGlobalData from '../../library/SftGlobalData';
import Colors from '../../library/Colors';
import StatsTiles from './StatsTiles';
import StatsSeriesChart from './StatsSeriesChart';
import StatsTable from './StatsTable';

/** Card size: backend hint, else tables with few columns take half the width. */
function sizeOf(kind, spec) {
  if (spec.size === 'half' || spec.size === 'full') return spec.size;
  if (kind === 'table') return (spec.columns || []).length <= 5 ? 'half' : 'full';
  return 'full';
}

/** Series and tables, in backend order, grouped by their 'group' label (order of first appearance). */
function groupItems(data) {
  const items = [
    ...(data.series || []).map((s) => ({ kind: 'series', spec: s })),
    ...(data.tables || []).map((tb) => ({ kind: 'table', spec: tb })),
  ];
  const groups = [];
  items.forEach((item) => {
    const name = item.spec.group || '';
    let g = groups.find((x) => x.name === name);
    if (!g) { g = { name, items: [] }; groups.push(g); }
    g.items.push(item);
  });
  // A half-width panel that ends up alone on its row (followed by a full one, or last) is centred,
  // except when it is the first panel of its group: under a group title it stays left-aligned.
  groups.forEach((g) => {
    let pending = null;
    const markAlone = (item) => { if (item !== g.items[0]) item.alone = true; };
    g.items.forEach((item) => {
      item.size = sizeOf(item.kind, item.spec);
      item.alone = false;
      if (item.size === 'full') {
        if (pending) markAlone(pending);
        pending = null;
      } else if (pending) {
        pending = null;            // two halves side by side
      } else {
        pending = item;
      }
    });
    if (pending) markAlone(pending);
  });
  return groups;
}

/**
 * Loads one statistics tab (adminStat&action=tab) for a period (+ region) and renders
 * tiles → series → tables. Reloads when the period, region or the reload counter changes.
 * SUPER users get the queries behind every table/chart ("i" button).
 */
export default function StatsTabPanel({ tab, period, group, userId, theme, t, reloadKey = 0, onLoaded }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const requestId = useRef(0);
  const region = period.region || '';
  const withExcluded = period.withExcluded ? 1 : 0;
  const [showSql, setShowSql] = useState(group === 'SUPER');

  useEffect(() => {
    if (!period.from || !period.to || period.from > period.to) return;
    const id = ++requestId.current;
    setLoading(true);
    setError(null);
    SftGlobalData.post('adminStat&action=tab', { sft_user_id: userId, sft_group: group, tab, from: period.from, to: period.to, region, include_excluded: withExcluded })
      .then((response) => {
        if (id !== requestId.current) return; // a newer request superseded this one
        if (!response || !response.success || !response.data) {
          setError((response && response.message) || (t ? t('stats_load_error') : 'Statistics could not be loaded.'));
          setLoading(false);
          return;
        }
        setData(response.data);
        // The backend decides who gets the queries (SUPER) – follow what it actually sent.
        if (response.data.access) setShowSql(response.data.access.group === 'SUPER');
        setLoading(false);
        if (onLoaded) onLoaded(response.data);
      })
      .catch(() => {
        if (id !== requestId.current) return;
        setError(t ? t('stats_load_error') : 'Statistics could not be loaded.');
        setLoading(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, period.from, period.to, region, withExcluded, group, userId, reloadKey]);

  if (loading && !data) {
    return (
      <div style={{ display: 'flex', justifyContent: 'space-evenly', marginTop: 5 }}>
        <TailSpin height="40" width="40" color={Colors.sft_blue} ariaLabel="loading" />
      </div>
    );
  }
  if (error) {
    return <p style={{ color: '#b02a2a', padding: '8px 12px' }}>{error}</p>;
  }
  if (!data) return null;

  return (
    <div style={{ opacity: loading ? 0.55 : 1, transition: 'opacity .2s', width: '100%', minWidth: 0 }}>
      {data.region_note && <p style={{ fontSize: 12.5, opacity: 0.75, margin: '0 0 8px', padding: '6px 10px', borderLeft: `3px solid ${Colors.sft_blue}` }}>{data.region_note}</p>}
      <StatsTiles tiles={data.tiles} theme={theme} t={t} />
      {groupItems(data).map((g) => (
        <div key={g.name || '_'} className="stats__group">
          {g.name && <h3 className={theme + ' stats__group_title'}>{g.name}</h3>}
          <div className="stats__grid">
            {g.items.map(({ kind, spec, size, alone }) => (
              <div key={kind + ':' + spec.key} className={'stats__panel ' + theme + ' stats__' + size + (alone ? ' stats__half_alone' : '')}>
                {kind === 'series'
                  ? <StatsSeriesChart series={spec} theme={theme} showSql={showSql} t={t} height={size === 'half' ? 250 : 290} />
                  : <StatsTable table={spec} theme={theme} loading={loading} showSql={showSql} t={t} drillContext={{ userId, group, period: { from: period.from, to: period.to, region, withExcluded } }} />}
              </div>
            ))}
          </div>
        </div>
      ))}
      {(!data.tiles || !data.tiles.length) && (!data.series || !data.series.length) && (!data.tables || !data.tables.length) && (
        <p style={{ opacity: 0.7, padding: '8px 12px' }}>{t ? t('stats_no_data') : 'No data for this period.'}</p>
      )}
    </div>
  );
}
